import React, { useState } from 'react';
import { FolderOpen, Check, X, RefreshCw, AlertCircle, ArrowRight, Briefcase, Search, Loader2 } from 'lucide-react';
import { approveClassification, rejectClassification, refileClassification, searchMatters } from '../../services/emailService';

const CaseFilingPanel = ({ emailId, intelligence, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState(null);
  const [showCasePicker, setShowCasePicker] = useState(false);
  const [caseSearch, setCaseSearch] = useState('');
  const [cases, setCases] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [result, setResult] = useState(null);

  if (!intelligence?.matched_case_id) return null;

  const confidence = intelligence.match_confidence || 0;
  const confidencePercent = Math.round(confidence * 100);

  const getConfidenceColor = (conf) => {
    if (conf >= 0.9) return { bg: 'from-green-500/20 to-emerald-500/20', border: 'border-green-500/30', text: 'text-green-400', bar: 'bg-green-500' };
    if (conf >= 0.7) return { bg: 'from-amber-500/20 to-yellow-500/20', border: 'border-amber-500/30', text: 'text-amber-400', bar: 'bg-amber-500' };
    return { bg: 'from-red-500/20 to-orange-500/20', border: 'border-red-500/30', text: 'text-red-400', bar: 'bg-red-500' };
  };

  const colors = getConfidenceColor(confidence);

  const handleApprove = async () => {
    setLoading(true);
    setAction('approve');
    try {
      const data = await approveClassification(emailId);
      setResult({ type: 'success', message: `Filed to ${data.case_name}` });
      onUpdate && onUpdate();
    } catch (err) {
      setResult({ type: 'error', message: err.response?.data?.detail || 'Failed to approve' });
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    setLoading(true);
    setAction('reject');
    try {
      await rejectClassification(emailId, { reason: 'user_rejected' });
      setResult({ type: 'success', message: 'Suggestion dismissed' });
      onUpdate && onUpdate();
    } catch (err) {
      setResult({ type: 'error', message: err.response?.data?.detail || 'Failed to dismiss' });
    } finally {
      setLoading(false);
    }
  };

  const handleRefile = async (newCaseId) => {
    setLoading(true);
    setAction('refile');
    try {
      const data = await refileClassification(emailId, { new_case_id: newCaseId });
      setResult({ type: 'success', message: `Refiled to ${data.case_name}` });
      setShowCasePicker(false);
      onUpdate && onUpdate();
    } catch (err) {
      setResult({ type: 'error', message: err.response?.data?.detail || 'Failed to refile' });
    } finally {
      setLoading(false);
    }
  };

  const handleSearchCases = async (query) => {
    setCaseSearch(query);
    if (query.length < 2) { setCases([]); return; }
    setSearchLoading(true);
    try {
      const data = await searchMatters(query, 10);
      setCases(data || []);
    } catch (err) {
      console.error('Case search failed:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  if (result) {
    return (
      <div className={`rounded-lg p-4 ${result.type === 'success' ? 'bg-green-500/10 border border-green-500/20' : 'bg-red-500/10 border border-red-500/20'}`}>
        <div className="flex items-center gap-2">
          {result.type === 'success' ? (
            <Check className="w-4 h-4 text-green-400" />
          ) : (
            <AlertCircle className="w-4 h-4 text-red-400" />
          )}
          <span className={result.type === 'success' ? 'text-green-400' : 'text-red-400'}>
            {result.message}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-gradient-to-r ${colors.bg} ${colors.border} border rounded-lg p-4`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FolderOpen className="w-4 h-4 text-cyan-400" />
          <span className="text-sm font-medium text-cyan-400">Case Match Suggestion</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-24 h-2 bg-slate-700 rounded-full overflow-hidden">
            <div className={`h-full ${colors.bar} rounded-full transition-all`} style={{ width: `${confidencePercent}%` }} />
          </div>
          <span className={`text-xs font-medium ${colors.text}`}>{confidencePercent}%</span>
        </div>
      </div>

      <div className="bg-slate-800/50 rounded-lg p-3 mb-3">
        <div className="flex items-center gap-2 mb-1">
          <Briefcase className="w-4 h-4 text-cyan-400" />
          <span className="text-sm font-semibold text-white">
            {intelligence.case_name || 'Unknown Case'}
          </span>
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          {intelligence.display_number && (
            <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded-full">{intelligence.display_number}</span>
          )}
          {intelligence.practice_area && (
            <span className="px-2 py-0.5 bg-slate-600/50 text-slate-300 rounded-full">{intelligence.practice_area}</span>
          )}
          {intelligence.case_status && (
            <span className={`px-2 py-0.5 rounded-full ${intelligence.case_status === 'active' ? 'bg-green-500/20 text-green-300' : 'bg-slate-600/50 text-slate-300'}`}>
              {intelligence.case_status}
            </span>
          )}
        </div>
      </div>

      {intelligence.match_reasoning && (
        <p className="text-xs text-slate-400 mb-3 leading-relaxed">{intelligence.match_reasoning}</p>
      )}

      {!showCasePicker ? (
        <div className="flex items-center gap-2">
          <button onClick={handleApprove} disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
            {loading && action === 'approve' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            File to Case
          </button>
          <button onClick={() => setShowCasePicker(true)} disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-600 hover:bg-slate-500 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
            <RefreshCw className="w-3.5 h-3.5" /> Wrong Case
          </button>
          <button onClick={handleReject} disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
            {loading && action === 'reject' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
            Dismiss
          </button>
        </div>
      ) : (
        <div className="bg-slate-800/70 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <Search className="w-4 h-4 text-slate-400" />
            <input type="text" value={caseSearch} onChange={(e) => handleSearchCases(e.target.value)}
              placeholder="Search cases..." autoFocus
              className="flex-1 bg-transparent border-none text-white text-sm placeholder-slate-500 focus:outline-none" />
            <button onClick={() => setShowCasePicker(false)} className="text-slate-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="max-h-40 overflow-y-auto space-y-1">
            {searchLoading ? (
              <div className="flex items-center justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-cyan-400" /></div>
            ) : cases.length > 0 ? (
              cases.map((c) => (
                <button key={c.id} onClick={() => handleRefile(c.id)} disabled={loading}
                  className="w-full text-left px-2 py-1.5 rounded bg-slate-700/50 hover:bg-blue-500/20 hover:border-blue-500/30 border border-transparent transition-all disabled:opacity-50">
                  <div className="text-xs text-blue-400">{c.display_number || c.client_name}</div>
                  <div className="text-sm text-white">{c.case_name || c.title}</div>
                </button>
              ))
            ) : caseSearch.length >= 2 ? (
              <p className="text-xs text-slate-500 text-center py-2">No cases found</p>
            ) : (
              <p className="text-xs text-slate-500 text-center py-2">Type to search...</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseFilingPanel;
